import React, { useState, useEffect } from 'react';
import PropertyForm from '../components/PropertyForm';
import ResultsView from '../components/ResultsView';
import AnalyticsView from '../components/AnalyticsView';
import Loader from '../components/Loader';
import { generateContent } from '../utils/aiGenerator';
import { supabase } from '../supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const TABLE_NAME = 'listings_final_v1';

const ExecutiveDashboard = () => {
  const [activeTab, setActiveTab] = useState('listings');
  const [view, setView] = useState('list');
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState([]);
  const [currentResults, setCurrentResults] = useState(null);
  const [lastFormData, setLastFormData] = useState(null);
  const [editingListing, setEditingListing] = useState(null);

  useEffect(() => {
    fetchMyListings();
  }, []);

  const fetchMyListings = async () => {
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setListings(data || []);
    } catch (err) {
      console.error("Error cargando propiedades:", err);
    }
  };

  const handleStatusChange = async (id, currentStatus) => {
    const statuses = ['Disponible', 'Rentado', 'Vendido'];
    const currentIndex = statuses.indexOf(currentStatus || 'Disponible');
    const nextStatus = statuses[(currentIndex + 1) % statuses.length];

    try {
      await supabase
        .from(TABLE_NAME)
        .update({ 
          status: nextStatus, 
          status_updated_at: new Date().toISOString() 
        })
        .eq('id', id);
      fetchMyListings();
    } catch (error) {
      alert('Error al actualizar estado: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta propiedad?')) return;
    try {
      await supabase.from(TABLE_NAME).delete().eq('id', id);
      fetchMyListings();
    } catch (error) {
      alert('Error al eliminar: ' + error.message);
    }
  };

  const handleGenerateAndSave = async (formData) => {
    setLoading(true);
    setLastFormData(formData);
    try {
      const generated = await generateContent(formData);
      const { data: { user } } = await supabase.auth.getUser();
      
      const payload = {
        property_type: formData.propertyType,
        operation: formData.operation,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        price: Number(formData.price),
        currency: formData.currency,
        bedrooms: Number(formData.bedrooms),
        bathrooms: Number(formData.bathrooms),
        area: Number(formData.area),
        area_unit: formData.areaUnit,
        parking: Number(formData.parking),
        amenities: formData.amenities,
        images: formData.images,
        cover_index: formData.coverIndex,
        custom_flyer: formData.customFlyer,
        agent_name: formData.agentName,
        agent_phone: formData.agentPhone,
        agent_email: formData.agentEmail,
        description: generated.description,
        instagram_copy: generated.instagram,
        agent_id: user.id
      };

      if (editingListing) {
        await supabase.from(TABLE_NAME).update(payload).eq('id', editingListing.id);
      } else {
        await supabase.from(TABLE_NAME).insert([{ ...payload, status: 'Disponible' }]);
      }

      setCurrentResults(generated);
      setView('success');
      fetchMyListings();
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 md:py-10">
      <div className="flex flex-col gap-6 mb-8 md:mb-10">
        <div className="text-center md:text-left">
          <h1 className="text-2xl md:text-3xl font-black text-emerald-950 tracking-tighter uppercase">
            Panel de Gestión
          </h1>
          <p className="text-xs md:text-sm text-gray-500 font-medium">Administra el inventario y analiza el rendimiento.</p>
        </div>
        
        <div className="bg-gray-100 p-1 rounded-xl md:rounded-2xl flex w-full md:w-fit mx-auto md:mx-0">
          <button
            onClick={() => { setActiveTab('listings'); setView('list'); }}
            className={`flex-1 md:flex-none px-4 md:px-6 py-2 md:py-2.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === 'listings' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500'}`}
          >
            <SafeIcon icon={FiIcons.FiList} /> Inventario
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex-1 md:flex-none px-4 md:px-6 py-2 md:py-2.5 rounded-lg md:rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${activeTab === 'analytics' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500'}`}
          >
            <SafeIcon icon={FiIcons.FiBarChart2} /> Estadísticas
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'listings' ? (
          <motion.div 
            key="listings-view"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {view === 'list' && (
              <div className="space-y-6">
                <div className="flex justify-end">
                  <button 
                    onClick={() => { setEditingListing(null); setView('create'); }}
                    className="w-full md:w-auto bg-emerald-600 text-white px-6 py-3.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiIcons.FiPlus} /> Añadir Propiedad
                  </button>
                </div>

                {/* Vista de Escritorio (Tabla) */}
                <div className="hidden md:block bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
                  <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Propiedad</th>
                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">Estado</th>
                        <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {listings.map(item => (
                        <tr key={item.id} className="hover:bg-gray-50/50 transition-colors group">
                          <td className="px-8 py-5">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-xl overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                                <img src={item.images?.[item.cover_index]?.src} className="w-full h-full object-cover" alt="Cover" />
                              </div>
                              <div>
                                <p className="font-black text-gray-900 group-hover:text-emerald-700 transition-colors">{item.property_type} en {item.city}</p>
                                <p className="text-xs text-gray-500 font-medium">{item.operation} • {item.currency} {Number(item.price).toLocaleString()}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-8 py-5 text-center">
                            <button 
                              onClick={() => handleStatusChange(item.id, item.status)}
                              className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-full transition-all border ${
                                item.status === 'Disponible' || !item.status 
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100' 
                                  : item.status === 'Vendido' 
                                  ? 'bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100' 
                                  : 'bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100'
                              }`}
                            >
                              {item.status || 'Disponible'}
                            </button>
                          </td>
                          <td className="px-8 py-5 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button 
                                onClick={() => { setEditingListing(item); setView('create'); }}
                                className="p-2.5 text-gray-400 hover:bg-emerald-50 hover:text-emerald-600 rounded-xl transition-all"
                              >
                                <SafeIcon icon={FiIcons.FiEdit2} />
                              </button>
                              <button 
                                onClick={() => handleDelete(item.id)}
                                className="p-2.5 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                              >
                                <SafeIcon icon={FiIcons.FiTrash2} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Vista de Móvil (Tarjetas) */}
                <div className="md:hidden space-y-4">
                  {listings.map(item => (
                    <div key={item.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                          <img src={item.images?.[item.cover_index]?.src} className="w-full h-full object-cover" alt="Cover" />
                        </div>
                        <div className="flex-grow overflow-hidden">
                          <p className="font-black text-gray-900 truncate">{item.property_type} en {item.city}</p>
                          <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                            {item.operation} • {item.currency} {Number(item.price).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 pt-4 border-t border-gray-50">
                        <button 
                          onClick={() => handleStatusChange(item.id, item.status)}
                          className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-wider rounded-lg border ${
                            item.status === 'Disponible' || !item.status ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {item.status || 'Disponible'}
                        </button>
                        <div className="flex gap-2">
                          <button onClick={() => { setEditingListing(item); setView('create'); }} className="p-2.5 bg-gray-50 text-gray-600 rounded-lg">
                            <SafeIcon icon={FiIcons.FiEdit2} />
                          </button>
                          <button onClick={() => handleDelete(item.id)} className="p-2.5 bg-red-50 text-red-500 rounded-lg">
                            <SafeIcon icon={FiIcons.FiTrash2} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {listings.length === 0 && (
                  <div className="py-20 text-center bg-white rounded-3xl border border-dashed border-gray-200">
                    <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Inventario vacío</p>
                  </div>
                )}
              </div>
            )}

            {view === 'create' && (
              <PropertyForm onSubmit={handleGenerateAndSave} initialData={editingListing} />
            )}

            {view === 'success' && (
              <ResultsView results={currentResults} formData={lastFormData} onReset={() => setView('list')} />
            )}
          </motion.div>
        ) : (
          <motion.div key="analytics-view" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <AnalyticsView listings={listings} />
          </motion.div>
        )}
      </AnimatePresence>

      {loading && (
        <div className="fixed inset-0 z-[110] bg-white/80 backdrop-blur-sm flex items-center justify-center p-6">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default ExecutiveDashboard;